import {
  ROUTE_SCORING_VERSION,
  artifactVersionSchema,
  routeSeedSchema,
  type ArtifactVersion,
  type LocationSnapshot,
} from '@der-maschinfuehrer/contracts';

import { ACTIVE_CATALOG_VERSION } from '../domain/alpha-shift';
import type {
  NetworkRouteAvailability,
  NetworkRouteCandidateInput,
  NetworkRouteUnavailableReason,
} from '../domain/route-candidates';
import {
  NETWORK_GRAPH_SCHEMA,
  NETWORK_GRAPH_WORKER_PROTOCOL,
  WEB_NETWORK_GRAPH,
} from './generated-network-graph';
import type { NetworkGraphReadiness } from './network-graph-artifact';
import {
  isNetworkGraphWorkerResponse,
  type NetworkGraphWorkerRequest,
  type NetworkGraphWorkerResponse,
} from './network-graph-protocol';

interface PendingRequest {
  readonly reject: (reason: Error) => void;
  readonly resolve: (response: NetworkGraphWorkerResponse) => void;
  readonly timeout: ReturnType<typeof setTimeout>;
}

export interface NetworkRouteQuery {
  readonly destination: LocationSnapshot | null;
  readonly destinationCatalogDocumentId: string | null;
  readonly destinationCatalogVersion: ArtifactVersion | null;
  readonly origin: LocationSnapshot | null;
  readonly originCatalogDocumentId: string | null;
  readonly originCatalogVersion: ArtifactVersion | null;
}

export class NetworkGraphClientError extends Error {
  public constructor(
    public readonly reason: NetworkRouteUnavailableReason,
    message: string,
  ) {
    super(message);
    this.name = 'NetworkGraphClientError';
  }
}

export const ACTIVE_GRAPH_VERSION: ArtifactVersion = artifactVersionSchema.parse({
  version: WEB_NETWORK_GRAPH.formatVersion,
  sha256: WEB_NETWORK_GRAPH.graphVersion.replace(/^sha256-/u, ''),
});

export class NetworkRouteClient {
  readonly #generation = 1;
  #initialization: Promise<NetworkGraphReadiness> | null = null;
  readonly #pending = new Map<string, PendingRequest>();
  #requestSequence = 0;
  #worker: Worker | null = null;

  public prepare(): Promise<NetworkGraphReadiness> {
    return this.#initialize();
  }

  public async query(input: NetworkRouteQuery): Promise<NetworkRouteAvailability> {
    if (
      input.origin === null ||
      input.destination === null ||
      input.originCatalogDocumentId === null ||
      input.destinationCatalogDocumentId === null ||
      !sameArtifactVersion(input.originCatalogVersion, ACTIVE_CATALOG_VERSION) ||
      !sameArtifactVersion(input.destinationCatalogVersion, ACTIVE_CATALOG_VERSION)
    ) {
      return { status: 'ready_no_route' };
    }
    await this.#initialize();
    const response = await this.#send({
      protocol: NETWORK_GRAPH_WORKER_PROTOCOL,
      requestId: this.#requestId(),
      generation: this.#generation,
      type: 'query',
      originCatalogDocumentId: input.originCatalogDocumentId,
      destinationCatalogDocumentId: input.destinationCatalogDocumentId,
    });
    if (response.type === 'no_route' && response.graphVersion === WEB_NETWORK_GRAPH.graphVersion) {
      return { status: 'ready_no_route' };
    }
    if (
      response.type !== 'route' ||
      response.graphVersion !== WEB_NETWORK_GRAPH.graphVersion ||
      response.edges.length === 0 ||
      response.edges.length > 50_000
    ) {
      throw new NetworkGraphClientError('corrupt', 'Síťový Worker vrátil neplatnou trasu.');
    }
    const observedLength = response.edges.reduce((sum, edge) => sum + edge.lengthMetres, 0);
    if (observedLength !== response.lengthMetres) {
      throw new NetworkGraphClientError(
        'corrupt',
        'Délka síťového návrhu neodpovídá součtu železničních hran.',
      );
    }
    const routeSeed = routeSeedSchema.parse({
      candidate_source: 'network',
      via: [],
      network_segments: response.edges.map((edge, sequence) => ({
        sequence,
        graph_edge_id: edge.graphEdgeId,
        line_reference: `OSM way ${String(edge.osmWayId)}`,
        from_location_id: null,
        to_location_id: null,
        length_metres: edge.lengthMetres,
      })),
      distance: {
        metres: response.lengthMetres,
        provenance: 'network_graph',
        quality: 'estimated',
        calculation_basis: 'graph_estimate',
        source_artifact: ACTIVE_GRAPH_VERSION,
        source_record_id: null,
        user_confirmed_at: null,
      },
      catalog_version: ACTIVE_CATALOG_VERSION,
      graph_version: ACTIVE_GRAPH_VERSION,
      route_scoring_version: ROUTE_SCORING_VERSION,
    });
    const direction = `${input.originCatalogDocumentId}->${input.destinationCatalogDocumentId}`;
    const candidate: NetworkRouteCandidateInput = {
      offer_id: `network-${ACTIVE_GRAPH_VERSION.sha256.slice(0, 16)}-${direction}`,
      name: 'Síťová trasa CZ + DE',
      origin: input.origin,
      via: [],
      destination: input.destination,
      route_seed: routeSeed,
    };
    return { status: 'ready', candidates: [candidate] };
  }

  public dispose(): void {
    const worker = this.#worker;
    if (worker !== null) {
      try {
        worker.postMessage({
          protocol: NETWORK_GRAPH_WORKER_PROTOCOL,
          requestId: this.#requestId(),
          generation: this.#generation,
          type: 'dispose',
        } satisfies NetworkGraphWorkerRequest);
      } catch {
        // terminate() below is the definitive local cleanup boundary.
      }
    }
    this.#worker = null;
    this.#initialization = null;
    for (const pending of this.#pending.values()) {
      clearTimeout(pending.timeout);
      pending.reject(new NetworkGraphClientError('corrupt', 'Síťový Worker byl ukončen.'));
    }
    this.#pending.clear();
    worker?.terminate();
  }

  async #initialize(): Promise<NetworkGraphReadiness> {
    if (this.#initialization === null) {
      const attempt = this.#initializeOnce();
      this.#initialization = attempt;
      void attempt.catch(() => {
        if (this.#initialization === attempt) {
          this.#worker?.terminate();
          this.#worker = null;
          this.#initialization = null;
        }
      });
    }
    return this.#initialization;
  }

  async #initializeOnce(): Promise<NetworkGraphReadiness> {
    const worker = new Worker(new URL('../workers/network-graph-worker.ts', import.meta.url), {
      name: 'dmf-network-graph',
      type: 'module',
    });
    this.#worker = worker;
    worker.addEventListener('message', (event: MessageEvent<unknown>) => {
      this.#handleResponse(event.data);
    });
    worker.addEventListener('error', () => {
      this.#failPending(
        new NetworkGraphClientError(
          'corrupt',
          'Síťový Worker selhal; ruční trasa zůstává dostupná.',
        ),
      );
    });
    worker.addEventListener('messageerror', () => {
      this.#failPending(new NetworkGraphClientError('corrupt', 'Síťový Worker nepřijal odpověď.'));
    });
    const ready = await this.#send(
      {
        protocol: NETWORK_GRAPH_WORKER_PROTOCOL,
        requestId: this.#requestId(),
        generation: this.#generation,
        type: 'initialize',
        artifactUrl: WEB_NETWORK_GRAPH.artifactUrl,
        catalogVersion: WEB_NETWORK_GRAPH.catalogVersion,
        coreEdgeCount: WEB_NETWORK_GRAPH.coreEdgeCount,
        edgeCount: WEB_NETWORK_GRAPH.edgeCount,
        endpointConnectorEdgeCount: WEB_NETWORK_GRAPH.endpointConnectorEdgeCount,
        formatVersion: WEB_NETWORK_GRAPH.formatVersion,
        graphVersion: WEB_NETWORK_GRAPH.graphVersion,
        schemaVersion: NETWORK_GRAPH_SCHEMA,
        vertexCount: WEB_NETWORK_GRAPH.vertexCount,
      },
      120_000,
    );
    if (
      ready.type !== 'ready' ||
      ready.graphVersion !== WEB_NETWORK_GRAPH.graphVersion ||
      ready.coreEdgeCount !== WEB_NETWORK_GRAPH.coreEdgeCount ||
      ready.edgeCount !== WEB_NETWORK_GRAPH.edgeCount ||
      ready.endpointConnectorEdgeCount !== WEB_NETWORK_GRAPH.endpointConnectorEdgeCount ||
      ready.vertexCount !== WEB_NETWORK_GRAPH.vertexCount
    ) {
      throw new NetworkGraphClientError('corrupt', 'Síťový Worker nepotvrdil přesný graf.');
    }
    return {
      anchoredDocumentCount: ready.anchoredDocumentCount,
      artifactCompressedBytes: ready.artifactCompressedBytes,
      catalogDocumentCount: ready.catalogDocumentCount,
      coreEdgeCount: ready.coreEdgeCount,
      edgeCount: ready.edgeCount,
      endpointConnectorEdgeCount: ready.endpointConnectorEdgeCount,
      vertexCount: ready.vertexCount,
    };
  }

  #send(
    request: NetworkGraphWorkerRequest,
    timeoutMs = 30_000,
  ): Promise<NetworkGraphWorkerResponse> {
    const worker = this.#worker;
    if (worker === null) {
      return Promise.reject(new NetworkGraphClientError('corrupt', 'Síťový Worker není spuštěný.'));
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.#pending.delete(request.requestId);
        reject(new NetworkGraphClientError('corrupt', 'Síťový Worker neodpověděl včas.'));
      }, timeoutMs);
      this.#pending.set(request.requestId, { reject, resolve, timeout });
      worker.postMessage(request);
    });
  }

  #handleResponse(value: unknown): void {
    if (!isNetworkGraphWorkerResponse(value)) {
      this.#failPending(
        new NetworkGraphClientError('corrupt', 'Síťový Worker vrátil neplatná data.'),
      );
      return;
    }
    const pending = this.#pending.get(value.requestId);
    if (pending === undefined) return;
    clearTimeout(pending.timeout);
    this.#pending.delete(value.requestId);
    if (value.type === 'error') {
      pending.reject(
        new NetworkGraphClientError(
          value.code === 'incompatible'
            ? 'incompatible'
            : value.code === 'unavailable'
              ? 'not_prepared'
              : 'corrupt',
          value.message,
        ),
      );
    } else {
      pending.resolve(value);
    }
  }

  #failPending(error: Error): void {
    for (const pending of this.#pending.values()) {
      clearTimeout(pending.timeout);
      pending.reject(error);
    }
    this.#pending.clear();
  }

  #requestId(): string {
    this.#requestSequence += 1;
    return `network-${String(this.#requestSequence)}-${crypto.randomUUID()}`;
  }
}

function sameArtifactVersion(left: ArtifactVersion | null, right: ArtifactVersion): boolean {
  return left?.version === right.version && left.sha256 === right.sha256;
}
